<script setup lang="ts">
import { computed } from 'vue';

import StatusChip from './StatusChip.vue';
import type { Milestone, PlanSummary, Project, ProjectHealth } from '@/types/domain';
import { completionPercent, healthLabel, projectStage, projectStageLabel } from '@/utils/portfolio';

const props = defineProps<{
  project: Project;
  summary?: PlanSummary;
  health?: ProjectHealth;
  milestones?: Milestone[];
  conflict?: boolean;
  /** 管理员才看得到归档按钮——归档会让项目从组合、指挥台、健康度里一起消失 */
  canArchive?: boolean;
}>();
const emit = defineEmits<{ inspectHealth: []; archive: [project: Project] }>();

/*
  这张卡是「开发中」还是「草稿/还没正式开工」（0916 Tony 要的"直观"）。

  原来卡片上只有健康度灯，而**灯是照着在跑的项目那套算的**：线上 5 个草稿项目里
  DY26008 亮红灯、DY26005 亮黄灯——一个还没启动的项目在组合页上看着比真在跑的还急。
  这 5 个草稿同时贡献了 192 条「关键交付物没挂审批流」，却毫无标记。
*/
const stage = computed(() => projectStage(props.project));
const stageLabel = computed(() => projectStageLabel(props.project));

const progress = computed(() => completionPercent(props.summary, props.milestones));
/** 口径不同要说清楚，否则两个页面显示不同数字没人知道为什么 */
/*
  「已确认完成」不是「已完成」——一词之差是刻意的。

  全公司里程碑填报率只有 **12.3%**（285 个填了 35 个），计划节点更低（4160 个填了 79 个）。
  秦岭卡片上那个「0 / 13」不是一件事都没干：它有 232 个计划节点、349 个文件条目，
  只是**没有人回来填实际完成日期**。写成「已完成」会让每张卡片都像个死项目，
  看多了就没人信这个数字——比不显示更糟。
*/
const progressLabel = computed(() => (props.milestones?.length ? '里程碑已确认' : '计划完成'));
const reachedMilestones = computed(() =>
  (props.milestones ?? []).filter((item) => Boolean(item.actualDate)).length);

/*
  分母只数**排期过的**里程碑。
  全库 285 个里有 67 个连计划日期都没有，拿它们当分母是凭空把分数做小。
*/
const scheduledMilestones = computed(() =>
  (props.milestones ?? []).filter((item) => Boolean(item.planDate)).length);

/*
  逾期里程碑。

  卡片原来只说「9 / 13 个里程碑已达成」——**过期没完成的一个字都不提**。
  X04C 的 SOP 逾期 48 天、NS26009 的 GATE1 逾期 164 天，在卡片上都看不出来，
  只体现为分子小一点。和工作区顶上那条 Gate 用同一把尺子：
  有计划日期、没有实际日期、计划日期早于今天。
*/
const overdueMilestones = computed(() => {
  const today = new Date();
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return (props.milestones ?? []).filter((item) => {
    if (!item.planDate || item.actualDate) return false;
    const plan = new Date(item.planDate);
    if (Number.isNaN(plan.getTime())) return false;
    return Date.UTC(plan.getFullYear(), plan.getMonth(), plan.getDate()) < todayDay;
  }).length;
});
</script>

<template>
  <article class="project-card" :class="`stage-${stage}`">
    <div class="project-card-top">
      <button
        type="button"
        class="project-health-trigger"
        :aria-label="`查看${project.projectName}健康度明细`"
        @click="emit('inspectHealth')"
      >
        <StatusChip :value="health?.status || 'neutral'" :label="healthLabel(health?.status)" />
        <span>查看明细</span>
      </button>
      <span v-if="project.isCurrentUserMember" class="my-project-tag">我的项目</span>
      <span v-if="conflict" class="conflict-tag">编号冲突</span>
      <!--
        开发中的也标：只标草稿的话，没标的会被读成"还没加载出来"。
        灯管的是"有没有出事"，这个标签管的是"开没开工"，两件事不能共用一个颜色。
      -->
      <span class="stage-tag" :class="`is-${stage}`">{{ stageLabel }}</span>
    </div>
    <RouterLink class="project-card-main" :to="`/projects/${project.id}/overview`">
      <b>{{ project.projectNo }}</b>
      <h3>{{ project.projectName }}</h3>
      <p>{{ project.customer || '客户未维护' }}</p>
      <div class="project-progress">
        <span>
          <b>{{ progressLabel }}</b>
          <strong>{{ progress === null ? '未维护' : `${progress}%` }}</strong>
        </span>
<!-- 逾期排在前面：有 3 个过期没确认，比「达成 0 个」更该先看见 -->
        <small v-if="overdueMilestones" class="progress-overdue">
          ⚠ {{ overdueMilestones }} 个已过计划日期还没确认
        </small>
        <small v-if="scheduledMilestones" class="progress-detail">
          {{ reachedMilestones }} / {{ scheduledMilestones }} 个已排期的里程碑填了完成日期
        </small>
        <small v-else-if="milestones?.length" class="progress-detail">
          {{ milestones.length }} 个里程碑都还没排期
        </small>
        <i><b :style="{ width: progress === null ? '0%' : `${progress}%` }"></b></i>
      </div>
      <footer>
        <span>{{ summary?.phaseCount ? `${summary.phaseCount} 阶段` : '待补计划' }}</span>
        <b>进入工作区 →</b>
      </footer>
    </RouterLink>
    <button
      v-if="canArchive"
      type="button"
      class="card-archive"
      :title="`归档「${project.projectName}」`"
      @click.stop.prevent="emit('archive', project)"
    >归档</button>
  </article>
</template>

<style scoped>
/*
  归档按钮浮在卡片右上角，只有管理员看得到。
  放在 RouterLink 外面——套在链接里点它会顺带跳进工作区。
*/
.project-card { position: relative; }

/*
  草稿卡整张压暗 + 虚线边：一屏 12 张卡的时候，光靠右上角一个小标签
  扫过去是看不见的，得让"还没开工"这件事在**卡片形状**上就成立。
  开发中的一切照旧，不加装饰——常态不该比异常更显眼。
*/
.project-card.stage-not-started { border-style: dashed; background: repeating-linear-gradient(135deg, transparent, transparent 9px, rgba(15,23,36,.022) 9px, rgba(15,23,36,.022) 18px); }
.project-card.stage-not-started .project-card-main h3,
.project-card.stage-not-started .project-card-main b { color: var(--muted); }

.stage-tag { margin-left: auto; padding: 1px 7px; border: 1px solid var(--line); border-radius: 999px; font-size: 11px; font-weight: 700; line-height: 1.7; white-space: nowrap; }
.stage-tag.is-started { border-color: rgba(38,122,99,.35); background: rgba(38,122,99,.08); color: var(--green); }
.stage-tag.is-not-started { border-style: dashed; background: var(--paper); color: var(--muted); }
.stage-tag.is-archived { background: var(--paper); color: var(--muted); }
/* 逾期这条要看得见——原来卡片上只有「达成 N 个」，逾期完全不提 */
.progress-overdue { display: block; margin-top: 2px; color: #7a4a10; font-size: 11px; font-weight: 700; }
.card-archive {
  position: absolute; top: 10px; right: 10px; z-index: 1;
  padding: 3px 9px; border: 1px solid var(--line); border-radius: 3px;
  background: var(--white); color: var(--muted);
  font: 700 11px var(--body); cursor: pointer; opacity: 0;
}
.project-card:hover .card-archive, .card-archive:focus-visible { opacity: 1; }
.card-archive:hover { border-color: var(--risk); color: var(--risk); }
.project-card-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.project-card-main > b {
  margin-top: 15px;
  color: var(--blue);
  font: 800 11px var(--display);
  letter-spacing: .1em;
}

.project-card-main > p {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
}

/*
  「查看明细」是**在这一页展开健康度**，不是跳去别的页面——所以它长成按钮，
  而不是像卡片底部的「进入工作区 →」那样的跳转提示。
  状态徽章（异常/关注）留在按钮外面，它是状态不是操作。
*/
.project-health-trigger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.project-health-trigger > span:last-child {
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-s);
  color: var(--blue);
  background: var(--white);
  line-height: 1;
  white-space: nowrap;
  transition: border-color .15s, background-color .15s;
}

.project-health-trigger:hover > span:last-child { border-color: var(--blue); background: rgba(0,117,174,.07); }

.progress-detail { display: block; margin-top: 2px; color: var(--muted); font-size: 12px; }
</style>
